import { defineStyle, defineStyleConfig, SystemStyleInterpolation } from '@chakra-ui/styled-system';
import { rem } from '../../utils/utils';
import { ButtonProps } from './Button';

const ignoreSsrWarning =
  '/* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */';

const baseStyle = defineStyle((props) => {
  const style: SystemStyleInterpolation = {
    borderRadius: '4',
    '.chakra-button__group[data-attached][data-orientation=horizontal] > &': {
      borderRadius: 0,
      [`:first-child${ignoreSsrWarning}`]: {
        borderLeftRadius: '4',
      },
      // Selects the last .chakra-button item even is there an another element after that (eg menu)
      ':not(:has(~ .chakra-button))': {
        borderRightRadius: '4',
      },
      [`:not(:first-child)${ignoreSsrWarning}`]: {
        ...(props.variant.includes('primary')
          ? {
              _before: {
                content: `''`,
                pos: 'absolute',
                left: `-${rem(1)}`,
                top: `-${rem(1)}`,
                bottom: `-${rem(1)}`,
                width: rem(1),
                backgroundColor: 'border/on-contrast',
                zIndex: 1,
              },
            }
          : {}),
        marginInlineStart: `-${rem(1)}`,
      },
    },
  };

  return style;
});

const sizes = {
  lg: defineStyle((props) => {
    return {
      paddingBlock: rem(11),
      paddingInlineEnd: props.rightIcon ? rem(11) : rem(15),
      paddingInlineStart: props.leftIcon ? rem(11) : rem(15),
      textStyle: 'comp/button/lg',
      minWidth: 48,
      height: 48,
    };
  }),
  md: defineStyle((props) => {
    return {
      paddingBlock: rem(9),
      paddingInlineEnd: props.rightIcon ? rem(11) : rem(15),
      paddingInlineStart: props.leftIcon ? rem(11) : rem(15),
      textStyle: 'comp/button/md',
      minWidth: 40,
      height: 40,
    };
  }),
  sm: defineStyle((props) => {
    return {
      paddingBlock: rem(5),
      paddingInlineEnd: props.rightIcon ? rem(7) : rem(11),
      paddingInlineStart: props.leftIcon ? rem(7) : rem(11),
      textStyle: 'comp/button/sm',
      minWidth: 32,
      height: 32,
    };
  }),
};

const variants = (
  [
    'primary',
    'secondary',
    'tertiary',
    'danger-primary',
    'danger-secondary',
    'danger-tertiary',
  ] as ButtonProps['variant'][]
).reduce(
  (obj, variant) => {
    let borderColor = variant?.includes('secondary') ? `button/${variant}/border` : `button/${variant}/bg`;
    let disabledBorderColor = variant?.includes('secondary')
      ? `button/${variant}/border-disabled`
      : `button/${variant}/bg-disabled`;
    if (variant?.includes('tertiary')) {
      borderColor = 'transparent';
      disabledBorderColor = 'transparent';
    }
    obj[variant as NonNullable<ButtonProps['variant']>] = defineStyle({
      _hover: {
        backgroundColor: `button/${variant}/bg-hover`,
        borderColor: variant?.includes('secondary') ? `button/${variant}/border-hover` : `button/${variant}/bg-hover`,
        color: `button/${variant}/fg-hover`,
      },
      _active: {
        backgroundColor: `button/${variant}/bg-active`,
        borderColor: variant?.includes('secondary') ? `button/${variant}/border-active` : `button/${variant}/bg-active`,
        color: `button/${variant}/fg-active`,
      },
      _disabled: {
        backgroundColor: `button/${variant}/bg-disabled`,
        borderColor: disabledBorderColor,
        color: `button/${variant}/fg-disabled`,
        cursor: 'not-allowed',
      },
      backgroundColor: `button/${variant}/bg`,
      borderColor,
      borderStyle: 'solid',
      borderWidth: rem(1),
      color: `button/${variant}/fg`,
    });
    return obj;
  },
  {} as Record<NonNullable<ButtonProps['variant']>, SystemStyleInterpolation>,
);

const buttonTheme = defineStyleConfig({
  baseStyle,
  defaultProps: {
    size: 'lg',
    variant: 'primary',
  },
  sizes,
  variants,
});

export default buttonTheme;
